import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  IconButton,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  type ProjectFolder,
  useCreateFolder,
  useDeleteFolder,
  useFolderBoundaries,
  useProjectFolders,
  useUpdateFolder,
} from "../../api/projectFolders";
import {
  useArchiveProject,
  useArchivedProjects,
  useMoveProjectToFolder,
  usePermanentlyDeleteProject,
  useProjects,
  useRestoreProject,
  useSetProjectMapVisible,
} from "../../api/projects";
import { useAllStoryMaps, useCreateStoryMapScoped, useDeleteStoryMap } from "../../api/storyMaps";
import { useAuth } from "../../contexts/AuthContext";
import { DrawingProvider } from "../../contexts/DrawingContext";
import { MapProvider } from "../../contexts/MapContext";
import useDebounce from "../../hooks/useDebounce";
import { useHoverIntent } from "../../hooks/useHoverIntent";
import { useUiFlag } from "../../hooks/useUiFlag";
import { useUnitSystem } from "../../hooks/useUnitSystem";
import type { ApiError } from "../../types/api";
import type { Project } from "../../types/project";
import type { StoryContent, StoryMapSummary } from "../../types/storyMap";
import { hasCapability, isViewOnly } from "../../utils/permissions";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import { ProjectListingMapContent } from "../ProjectListingMap";
import CreateProjectPane from "../createProject/CreateProjectPane";
import NewStoryDialog from "../storyMap/NewStoryDialog";
import { storyMapPaths } from "../storyMap/storyPaths";
import type { StoryTemplateContext } from "../storyMap/storyTemplates";
import FolderBoundaryDialog from "./FolderBoundaryDialog";
import FolderPickerDialog from "./FolderPickerDialog";
import PropertyStyleDialog from "./PropertyStyleDialog";
import NewProjectMenu from "./NewProjectMenu";
import ProjectTablePane, { type FolderRename, type NewRowDraft } from "./ProjectTablePane";
import RootStoriesCard from "./RootStoriesCard";
import SplitHandle from "./SplitHandle";
import StoryStackPopover from "./StoryStackPopover";
import StoryTablePane from "./StoryTablePane";
import { SPLIT_DEFAULT, clampRatio } from "./splitRatio";
import {
  type FolderKey,
  buildFolderIndex,
  descendantIds,
  folderMapVisible,
  isDescendant,
  normalizeFolderId,
  pathLabel,
  projectMapVisible,
  subtreeCounts,
} from "./folderTree";
import { homeBoundsFor } from "./homeRegion";
import { LISTING_TOUR_ANCHOR } from "./listingTour";
import { type Bbox, bboxIntersects, bboxOfProjects, projectBbox, unionBbox } from "./projectBbox";
import {
  type SortSpec,
  type StorySortSpec,
  parseSort,
  parseStorySort,
  serializeSort,
  serializeStorySort,
  sortProjects,
  sortStories,
} from "./sortRows";
import {
  type StoryMarkerGroup,
  buildStoryIndex,
  byUpdatedDesc,
  groupStoriesForMap,
  projectGroupKey,
  storiesInFolder,
  storyGroupKey,
  storyScopeLabel,
  subtreeStoryCount,
} from "./storyRows";
import { NEW_PROJECT_NAME, useNewProjectFlow } from "./useNewProjectFlow";
import { useProjectListingTour } from "./useProjectListingTour";
import type { FlyToRequest, FolderOverlay, OpenFolderBoundary } from "./useListingMapAnnotations";

interface ProjectListingSplitProps {
  /** The control that switches to the classic layout. */
  layoutToggle: React.ReactNode;
}

type Hovered =
  | { kind: "project"; id: string; source: "table" | "map" }
  | { kind: "folder"; id: number; source: "table" | "map" }
  /** A story row (browse mode). */
  | { kind: "story"; id: string; source: "table" | "map" }
  /** A thumbnail marker on the map: every story in the group (id = group key, "root" for the corner card). */
  | { kind: "storyGroup"; id: string; source: "table" | "map" };

const ROOT_GROUP_KEY = "root";

const sameHovered = (a: Hovered | null, b: Hovered | null) =>
  a === b || (!!a && !!b && a.kind === b.kind && a.id === b.id && a.source === b.source);

type DialogState =
  | { kind: "moveFolder"; folder: ProjectFolder }
  | { kind: "deleteFolder"; folder: ProjectFolder }
  | { kind: "folderBoundary"; folder: ProjectFolder }
  | { kind: "moveProject"; project: Project }
  | { kind: "propertyStyle"; project: Project }
  | { kind: "deleteStory"; story: StoryMapSummary }
  | { kind: "deleteProjectForever"; project: Project }
  | { kind: "newStory" }
  | { kind: "storyStack"; title: string; stories: StoryMapSummary[]; anchorEl: HTMLElement }
  | null;

/** A new folder's starting name; "New Folder 2" and on when that is taken. */
const NEW_FOLDER_NAME = "New Folder";

/** A folder being named in the table before it exists (see NewFolderRow). */
interface DraftFolder {
  key: number;
  parent: FolderKey;
  name: string;
  /** The create request is in flight. */
  sending: boolean;
  error: string | null;
}

/** A folder being renamed in place (see InlineNameField). */
interface RenameState {
  folderId: number;
  /** The folder being looked at when it started: the row's parent, or the folder itself in the breadcrumb. */
  where: FolderKey;
  sending: boolean;
  error: string | null;
}

const EMPTY_PROJECTS: Project[] = [];
const EMPTY_FOLDERS: ProjectFolder[] = [];
const EMPTY_STORIES: StoryMapSummary[] = [];
const EMPTY_GROUPS: StoryMarkerGroup[] = [];
const EMPTY_IDS: ReadonlySet<number> = new Set();
const EMPTY_KEYS: ReadonlySet<string> = new Set();

const errorMessage = (e: unknown): string =>
  (e as ApiError | undefined)?.message || "Something went wrong. Please try again.";

/**
 * The map + list view of the project listing.
 *
 * Owns every piece of shared state: the open folder (in the URL, so Back
 * walks up the tree), the search, the hover that links the two panes, the
 * split ratio and the dialogs. The map pane draws what it is given and
 * reports hovers; the table pane renders rows and reports actions.
 */
export default function ProjectListingSplit({ layoutToggle }: ProjectListingSplitProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const viewOnly = isViewOnly(user);
  const canCreateProject = !viewOnly && (!!user?.is_staff || hasCapability(user, "create_project"));
  // Metric users are in Australia, imperial users in the United States.
  const unitSystem = useUnitSystem();
  const homeBounds = useMemo(() => homeBoundsFor(unitSystem), [unitSystem]);

  // -- filters -------------------------------------------------------------------
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const debouncedSearch = useDebounce(searchQuery, 500);
  const tagFilter = selectedTags.join(",");

  const { projects, isLoading, error } = useProjects(debouncedSearch, tagFilter);
  // Archived projects keep their folder but live in the listing's Archive, a
  // folder-like place at the top level (owner's call 2026-10-09, replacing
  // the Active/Archived tabs).
  const { projects: archivedProjects, isLoading: archivedLoading } = useArchivedProjects(debouncedSearch, tagFilter);
  const { folders, isLoading: foldersLoading } = useProjectFolders();
  const all = projects?.data ?? EMPTY_PROJECTS;
  const archivedAll = archivedProjects?.data ?? EMPTY_PROJECTS;
  const index = useMemo(() => buildFolderIndex(folders), [folders]);

  // -- what the listing browses: projects, or story maps -----------------------
  // Remembered per person. Story maps are filed into the same folders: a
  // project's stories where the project is, a folder's own stories in it,
  // organisation-wide ones at the top.
  const browseFlag = useUiFlag("projectListingBrowse");
  const browse: "projects" | "stories" = browseFlag.value === "stories" ? "stories" : "projects";
  const storiesQuery = useAllStoryMaps(browse === "stories");
  // Every project, unfiltered, so a story's project (and folder) resolves
  // even while a search narrows the project list.
  const { projects: everyProjectResp } = useProjects();
  const everyProject = everyProjectResp?.data ?? EMPTY_PROJECTS;
  const projectsById = useMemo(() => {
    const m = new Map<number, Project>();
    everyProject.forEach((p) => m.set(Number(p.id), p));
    archivedAll.forEach((p) => m.set(Number(p.id), p));
    return m;
  }, [everyProject, archivedAll]);
  const storyIndex = useMemo(
    () => buildStoryIndex(storiesQuery.data?.data ?? EMPTY_STORIES, everyProject, index),
    [storiesQuery.data, everyProject, index]
  );
  const storyCountFor = useCallback(
    (folderId: number) => subtreeStoryCount(storyIndex, index, folderId),
    [storyIndex, index]
  );

  const currentTags = useMemo(() => {
    const tagSet = new Set<string>();
    all.forEach((p) => p.tags?.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [all]);

  // -- open folder lives in the URL ------------------------------------------
  // ``?folder=archive`` opens the Archive.
  const [params, setParams] = useSearchParams();
  const rawFolder = params.get("folder");
  const archiveOpen = rawFolder === "archive";
  const requestedFolder = rawFolder && !archiveOpen ? Number(rawFolder) : null;
  const openFolderId: FolderKey =
    requestedFolder !== null && index.byId.has(requestedFolder) ? requestedFolder : null;
  useEffect(() => {
    // A folder that no longer exists (deleted elsewhere) falls back to root.
    if (foldersLoading || requestedFolder === null || index.byId.has(requestedFolder)) return;
    const next = new URLSearchParams(params);
    next.delete("folder");
    setParams(next, { replace: true });
  }, [foldersLoading, requestedFolder, index, params, setParams]);

  const openFolder = useCallback(
    (id: FolderKey) => {
      const next = new URLSearchParams(params);
      if (id === null) next.delete("folder");
      else next.set("folder", String(id));
      setParams(next);
    },
    [params, setParams]
  );
  const openArchive = useCallback(() => {
    const next = new URLSearchParams(params);
    next.set("folder", "archive");
    setParams(next);
  }, [params, setParams]);

  // -- what is visible --------------------------------------------------------
  const mode: "explorer" | "search" = debouncedSearch || selectedTags.length ? "search" : "explorer";
  // Inside the Archive the rows are the archived projects (the server already
  // applies the search and tags to that list).
  const visibleProjects = useMemo(
    () =>
      archiveOpen
        ? archivedAll
        : mode === "search"
          ? all
          : all.filter((p) => normalizeFolderId(index, p.folder) === openFolderId),
    [archiveOpen, archivedAll, mode, all, index, openFolderId]
  );
  const childFolders =
    mode === "explorer" && !archiveOpen ? index.childrenOf.get(openFolderId) ?? EMPTY_FOLDERS : EMPTY_FOLDERS;
  // What the map draws: the listed projects minus those hidden from the map
  // (their own flag, or a hidden folder above them). The table lists them all.
  const drawnProjects = useMemo(
    () => visibleProjects.filter((p) => projectMapVisible(index, p)),
    [visibleProjects, index]
  );

  const sortFlag = useUiFlag("projectListingSort");
  const sort = useMemo(() => parseSort(sortFlag.value), [sortFlag.value]);
  const folderLabelOf = useCallback(
    (p: Project) => pathLabel(index, normalizeFolderId(index, p.folder)),
    [index]
  );
  const rows = useMemo(() => sortProjects(visibleProjects, sort, folderLabelOf), [visibleProjects, sort, folderLabelOf]);

  // Story rows (browse mode). Searching matches a story's title, or its
  // project being in the (server-filtered) project results; a tag filter is
  // a project filter, so folder and organisation-wide stories sit it out.
  const visibleStories = useMemo(() => {
    // Archived projects' story maps are not available until the project is
    // restored (owner's call 2026-10-09), so the Archive lists none.
    if (browse !== "stories" || archiveOpen) return EMPTY_STORIES;
    if (mode === "explorer") return storiesInFolder(storyIndex, openFolderId);
    const q = debouncedSearch.trim().toLowerCase();
    const matchedProjects = new Set(all.map((p) => Number(p.id)));
    const out: StoryMapSummary[] = [];
    storyIndex.byId.forEach((story) => {
      const isProjectStory = story.project_id !== null && story.project_id !== undefined;
      const projectMatch = isProjectStory && matchedProjects.has(Number(story.project_id));
      if (selectedTags.length > 0) {
        if (projectMatch) out.push(story);
        return;
      }
      if (projectMatch || (q && story.title.toLowerCase().includes(q))) out.push(story);
    });
    return out.sort(byUpdatedDesc);
  }, [browse, mode, storyIndex, openFolderId, debouncedSearch, all, archiveOpen, selectedTags]);
  const storySortFlag = useUiFlag("projectListingStorySort");
  const storySort = useMemo(() => parseStorySort(storySortFlag.value), [storySortFlag.value]);
  const scopeLabelOf = useCallback((s: StoryMapSummary) => storyScopeLabel(s, index), [index]);
  const storyRows = useMemo(() => sortStories(visibleStories, storySort, scopeLabelOf), [visibleStories, storySort, scopeLabelOf]);

  // The camera re-frames only when the person goes somewhere: a folder, a
  // search, or the first time data arrives. A project leaving the set (moved
  // into a folder, archived) or arriving (polling) must not move the map.
  const hasData = !!projects?.data;
  const fitKey = useMemo(
    () => `${mode}|${openFolderId ?? "root"}|${debouncedSearch}|${tagFilter}|${hasData ? "loaded" : "loading"}`,
    [mode, openFolderId, debouncedSearch, tagFilter, hasData]
  );

  // -- hover: two speeds -----------------------------------------------------
  // `hovered` follows the pointer instantly and drives the highlights (row
  // tint, map outline, folder box): cheap and nothing moves. `settled` is the
  // same hover after the dwell, and drives the two things that do move the
  // screen: scrolling the row into view.
  const [hovered, setHovered] = useState<Hovered | null>(null);
  const [settled, setSettled] = useState<Hovered | null>(null);
  const intent = useHoverIntent<Hovered>(
    useCallback((v: Hovered | null) => setSettled((prev) => (sameHovered(prev, v) ? prev : v)), [])
  );
  const draggingRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const hoverFrom = useCallback(
    (v: Hovered | null) => {
      if (draggingRef.current) return;
      setHovered((prev) => (sameHovered(prev, v) ? prev : v));
      if (v === null) intent.leave();
      else intent.enter(v);
    },
    [intent]
  );
  const onDraggingChange = useCallback(
    (d: boolean) => {
      draggingRef.current = d;
      setDragging(d);
      if (d) {
        intent.cancel();
        setHovered(null);
        setSettled(null);
      }
    },
    [intent]
  );
  const onTableHoverProject = useCallback(
    (id: string | null) => hoverFrom(id === null ? null : { kind: "project", id, source: "table" }),
    [hoverFrom]
  );
  const onTableHoverFolder = useCallback(
    (id: number | null) => hoverFrom(id === null ? null : { kind: "folder", id, source: "table" }),
    [hoverFrom]
  );
  // Browsing stories, a project area on the map stands for that project's
  // stories: pointing at it lights their rows, like pointing at the marker.
  const browseRef = useRef(browse);
  browseRef.current = browse;
  const storyGroupKeysRef = useRef<ReadonlySet<string>>(EMPTY_KEYS);
  const onMapHoverProject = useCallback(
    (id: string | null) => {
      if (browseRef.current === "stories") {
        const key = id === null ? null : projectGroupKey(id);
        hoverFrom(key && storyGroupKeysRef.current.has(key) ? { kind: "storyGroup", id: key, source: "map" } : null);
        return;
      }
      hoverFrom(id === null ? null : { kind: "project", id, source: "map" });
    },
    [hoverFrom]
  );
  const onTableHoverStory = useCallback(
    (id: string | null) => hoverFrom(id === null ? null : { kind: "story", id, source: "table" }),
    [hoverFrom]
  );
  const onMapHoverStoryGroup = useCallback(
    (key: string | null) => hoverFrom(key === null ? null : { kind: "storyGroup", id: key, source: "map" }),
    [hoverFrom]
  );
  const onMapHoverFolder = useCallback(
    (id: number | null) => hoverFrom(id === null ? null : { kind: "folder", id, source: "map" }),
    [hoverFrom]
  );
  // Nothing stays highlighted across a folder, search or browse-mode change.
  useEffect(() => {
    intent.cancel();
    setHovered(null);
    setSettled(null);
  }, [fitKey, browse, intent]);

  // Extent of a folder's projects (recursive). Drawn ones by default, which is
  // what the folder box and the camera should follow; `everything` includes
  // projects hidden from the map, for the viewport filter and as a fallback.
  const folderBboxFor = useMemo(() => {
    const drawnCache = new Map<number, Bbox | null>();
    const allCache = new Map<number, Bbox | null>();
    return (id: number, everything = false): Bbox | null => {
      const cache = everything ? allCache : drawnCache;
      if (!cache.has(id)) {
        const ids = descendantIds(index, id);
        ids.add(id);
        cache.set(
          id,
          bboxOfProjects(
            all.filter((p) => {
              const f = normalizeFolderId(index, p.folder);
              return f !== null && ids.has(f) && (everything || projectMapVisible(index, p));
            })
          )
        );
      }
      return cache.get(id) ?? null;
    };
  }, [index, all]);

  // A folder's extent on the map: its projects' box plus the boundary layers
  // of the folder and every folder beneath it, so a folder whose only
  // content is a boundary still has somewhere to fly to.
  const folderExtentFor = useCallback(
    (id: number, everything = false): Bbox | null => {
      const ids = descendantIds(index, id);
      ids.add(id);
      const boxes: Array<Bbox | null> = [folderBboxFor(id, everything)];
      ids.forEach((fid) => {
        const f = index.byId.get(fid);
        if (f?.boundary_bbox && (everything || folderMapVisible(index, fid))) boxes.push(f.boundary_bbox);
      });
      return unionBbox(boxes);
    },
    [index, folderBboxFor]
  );

  // Where the camera goes for a folder: its projects (drawn ones first, then
  // any), never the boundary layer, which can be far bigger than the projects
  // inside it. The boundary is the fallback for a folder with no located
  // projects, so opening it still flies somewhere.
  const folderFlyExtentFor = useCallback(
    (id: number): Bbox | null => folderBboxFor(id) ?? folderBboxFor(id, true) ?? folderExtentFor(id, true),
    [folderBboxFor, folderExtentFor]
  );

  // The projects filed directly in a folder — not those in its sub-folders —
  // drawn ones by default.
  const directProjectsBboxFor = useCallback(
    (id: number, everything = false): Bbox | null =>
      bboxOfProjects(
        all.filter((p) => normalizeFolderId(index, p.folder) === id && (everything || projectMapVisible(index, p)))
      ),
    [all, index]
  );

  // What the camera frames on navigation: the search results, everything at
  // the top level, or — opening a folder — just the projects filed in it,
  // framed tightly (owner's call). A folder with no projects of its own
  // falls back to its sub-folders' projects, then to its boundary, so
  // opening it still flies to where its contents are.
  const fitTarget = useMemo(() => {
    if (mode === "search" || archiveOpen) return bboxOfProjects(drawnProjects) ?? bboxOfProjects(visibleProjects);
    if (openFolderId === null) return bboxOfProjects(all.filter((p) => projectMapVisible(index, p))) ?? bboxOfProjects(all);
    return (
      directProjectsBboxFor(openFolderId) ??
      directProjectsBboxFor(openFolderId, true) ??
      folderFlyExtentFor(openFolderId)
    );
  }, [mode, archiveOpen, drawnProjects, visibleProjects, all, index, openFolderId, directProjectsBboxFor, folderFlyExtentFor]);

  // Boundary layers to draw: the open folder's own and those of its
  // sub-folders that are on the map. Only folders that have one are asked for.
  const boundaryFolderIds = useMemo(() => {
    if (mode !== "explorer") return [] as number[];
    const ids: number[] = [];
    const open = openFolderId === null ? null : index.byId.get(openFolderId);
    if (open?.boundary_bbox && folderMapVisible(index, open.id)) ids.push(open.id);
    childFolders.forEach((f) => {
      if (f.boundary_bbox && folderMapVisible(index, f.id)) ids.push(f.id);
    });
    return ids;
  }, [mode, openFolderId, index, childFolders]);
  const folderBoundaries = useFolderBoundaries(boundaryFolderIds);
  const openFolderBoundary = useMemo<OpenFolderBoundary | null>(() => {
    if (openFolderId === null) return null;
    const b = folderBoundaries.get(openFolderId);
    if (!b?.geometry) return null;
    return { id: b.id, name: b.name, geometry: b.geometry, style: index.byId.get(openFolderId)?.boundary_style ?? null };
  }, [openFolderId, folderBoundaries, index]);

  // Every sub-folder with a mappable extent or a boundary layer, drawn
  // faintly on the map: a dashed box around its projects and, when it has
  // one (and it has loaded), its boundary layer too.
  const folderOverlays = useMemo<FolderOverlay[]>(
    () =>
      childFolders.flatMap((folder) => {
        if (!folderMapVisible(index, folder.id)) return [];
        const bbox = folderBboxFor(folder.id);
        const boundary = folderBoundaries.get(folder.id);
        if (!bbox && !boundary) return [];
        return [
          {
            id: folder.id,
            name: folder.name,
            bbox,
            projectCount: subtreeCounts(index, all, folder.id).projects,
            boundary: boundary?.geometry ?? null,
            boundaryBbox: boundary?.bbox ?? folder.boundary_bbox,
            boundaryStyle: folder.boundary_style ?? null,
          },
        ];
      }),
    [childFolders, folderBboxFor, folderBoundaries, index, all]
  );

  // The "only what's on the map" filter, switched on from a button on the
  // map (off by default: moving the map should not quietly shorten the list).
  // While on, rows whose extent misses the map's current view are set aside
  // behind a banner; projects with no location can't be out of view, so they
  // always stay.
  const [viewport, setViewport] = useState<Bbox | null>(null);
  const [onlyInView, setOnlyInView] = useState(false);
  const inView = useCallback(
    (b: Bbox | null) => !viewport || !b || bboxIntersects(b, viewport),
    [viewport]
  );
  const listedRows = useMemo(
    () => (onlyInView ? rows.filter((p) => inView(projectBbox(p))) : rows),
    [rows, onlyInView, inView]
  );
  const listedFolders = useMemo(
    () => (onlyInView ? childFolders.filter((f) => inView(folderExtentFor(f.id, true))) : childFolders),
    [childFolders, onlyInView, inView, folderExtentFor]
  );
  const viewFilter = {
    active: onlyInView,
    hiddenProjects: rows.length - listedRows.length,
    hiddenFolders: childFolders.length - listedFolders.length,
    onShowAll: () => setOnlyInView(false),
  };

  // Where a story sits on the map: its project's area, its folder's extent,
  // or nowhere (an organisation-wide story, an unlocated project).
  const storyExtentFor = useCallback(
    (story: StoryMapSummary): Bbox | null => {
      if (story.project_id !== null && story.project_id !== undefined) {
        const project = projectsById.get(Number(story.project_id));
        return project ? projectBbox(project) : null;
      }
      if (story.folder_id !== null && story.folder_id !== undefined) return folderExtentFor(story.folder_id, true);
      return null;
    },
    [projectsById, folderExtentFor]
  );
  const listedStoryRows = useMemo(
    () => (onlyInView ? storyRows.filter((s) => inView(storyExtentFor(s))) : storyRows),
    [storyRows, onlyInView, inView, storyExtentFor]
  );
  const storyViewFilter = {
    active: onlyInView,
    hiddenStories: storyRows.length - listedStoryRows.length,
    hiddenFolders: childFolders.length - listedFolders.length,
    onShowAll: () => setOnlyInView(false),
  };
  // Thumbnail markers: the listed stories grouped on their project or folder,
  // honouring the eyes like the areas themselves; organisation-wide stories go
  // to the corner card.
  const storyDrawn = useCallback(
    (story: StoryMapSummary) => {
      if (story.project_id !== null && story.project_id !== undefined) {
        const project = projectsById.get(Number(story.project_id));
        return !!project && projectMapVisible(index, project);
      }
      if (story.folder_id !== null && story.folder_id !== undefined) return folderMapVisible(index, story.folder_id);
      return true;
    },
    [projectsById, index]
  );
  const { storyGroups, rootStories } = useMemo(() => {
    if (browse !== "stories") return { storyGroups: EMPTY_GROUPS, rootStories: EMPTY_STORIES };
    const { groups } = groupStoriesForMap(
      listedStoryRows,
      projectsById,
      index,
      (fid) => folderExtentFor(fid, true),
      storyDrawn
    );
    return {
      storyGroups: groups,
      rootStories: listedStoryRows.filter((s) => s.project_id == null && s.folder_id == null),
    };
  }, [browse, listedStoryRows, projectsById, index, folderExtentFor, storyDrawn]);
  storyGroupKeysRef.current = useMemo(() => new Set(storyGroups.map((g) => g.key)), [storyGroups]);

  const [mapExpanded, setMapExpanded] = useState(false);
  // Instant: highlights.
  const hoveredProjectId = hovered?.kind === "project" ? hovered.id : null;
  const hoveredFolderId = hovered?.kind === "folder" ? hovered.id : null;
  // A hovered story lights its marker; a hovered marker (or the project area
  // under it) lights every story row in the group.
  const groupStories = useCallback(
    (key: string): StoryMapSummary[] =>
      key === ROOT_GROUP_KEY ? rootStories : storyGroups.find((g) => g.key === key)?.stories ?? EMPTY_STORIES,
    [rootStories, storyGroups]
  );
  const highlightedStoryIds = useMemo<ReadonlySet<string>>(() => {
    if (hovered?.kind === "story") return new Set([hovered.id]);
    if (hovered?.kind === "storyGroup") return new Set(groupStories(hovered.id).map((s) => s.id));
    return EMPTY_KEYS;
  }, [hovered, groupStories]);
  const hoveredGroupKeys = useMemo<ReadonlySet<string>>(() => {
    if (hovered?.kind === "storyGroup") return new Set([hovered.id]);
    if (hovered?.kind === "story") {
      const story = storyIndex.byId.get(hovered.id);
      const key = story ? storyGroupKey(story) ?? ROOT_GROUP_KEY : null;
      return key ? new Set([key]) : EMPTY_KEYS;
    }
    return EMPTY_KEYS;
  }, [hovered, storyIndex]);
  // After the dwell: a map hover scrolls the table row into view. Hovering
  // never moves the map; that takes the row's "show on map" button.
  const scrollToProjectId =
    settled?.kind === "project" && settled.source === "map" && !mapExpanded ? settled.id : null;
  const scrollToStoryId =
    settled?.kind === "storyGroup" && settled.source === "map" && !mapExpanded
      ? groupStories(settled.id)[0]?.id ?? null
      : null;
  const [flyTo, setFlyTo] = useState<FlyToRequest | null>(null);
  const flyToBounds = useCallback((bounds: Bbox | null) => {
    if (!bounds) return;
    setFlyTo((prev) => ({ bounds, seq: (prev?.seq ?? 0) + 1 }));
  }, []);
  const locateProject = useCallback((project: Project) => flyToBounds(projectBbox(project)), [flyToBounds]);

  // -- map expand / split ratio ----------------------------------------------
  const placementActiveRef = useRef(false);
  useEffect(() => {
    if (!mapExpanded) return;
    const onKey = (e: KeyboardEvent) => {
      // Escape also cancels the map's click-to-place modes; let those win.
      if (e.key === "Escape" && !placementActiveRef.current) setMapExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mapExpanded]);

  const splitFlag = useUiFlag("projectListingSplit");
  const [ratio, setRatio] = useState(() =>
    clampRatio(typeof splitFlag.value === "number" ? splitFlag.value : SPLIT_DEFAULT)
  );
  const gridRef = useRef<HTMLDivElement | null>(null);
  const gridTemplateColumns = mapExpanded
    ? "minmax(0, 1fr) 0px 0px"
    : `minmax(0, ${ratio}fr) 10px minmax(0, ${1 - ratio}fr)`;

  // -- tour ---------------------------------------------------------------------
  const [mapReady, setMapReady] = useState(false);
  const tour = useProjectListingTour({
    ready: !isLoading && !!projects?.data && mapReady,
  });

  // -- mutations + dialogs ----------------------------------------------------
  const createFolder = useCreateFolder();
  const updateFolder = useUpdateFolder();
  // Its own instance: per-call callbacks only fire for a hook's latest call,
  // and a pin or eye toggle mid-rename would otherwise swallow the rename's.
  const renameFolder = useUpdateFolder();
  const deleteFolder = useDeleteFolder();
  const moveProject = useMoveProjectToFolder();
  const setProjectMapVisible = useSetProjectMapVisible();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  /** A confirmation ("Share link copied"), as opposed to an error toast. */
  const [notice, setNotice] = useState<string | null>(null);
  const openDialog = (next: DialogState) => {
    setDialogError(null);
    setDialog(next);
  };
  const closeDialog = () => {
    setDialog(null);
    setDialogError(null);
  };

  // -- new folder: named in place in the table --------------------------------
  const [draftFolder, setDraftFolder] = useState<DraftFolder | null>(null);
  const [renaming, setRenaming] = useState<RenameState | null>(null);
  const draftSeqRef = useRef(0);
  const openFolderIdRef = useRef(openFolderId);
  openFolderIdRef.current = openFolderId;
  const startNewFolder = () => {
    const taken = new Set((index.childrenOf.get(openFolderId) ?? []).map((f) => f.name.trim().toLowerCase()));
    let name = NEW_FOLDER_NAME;
    for (let n = 2; taken.has(name.toLowerCase()); n++) name = `${NEW_FOLDER_NAME} ${n}`;
    // The row lives in the folder list, which only shows outside a search.
    setMapExpanded(false);
    setSearchQuery("");
    setSelectedTags([]);
    draftSeqRef.current += 1;
    setDraftFolder({ key: draftSeqRef.current, parent: openFolderId, name, sending: false, error: null });
  };
  const sameDraft = (key: number, update: (d: DraftFolder) => DraftFolder | null) =>
    setDraftFolder((d) => (d?.key === key ? update(d) : d));
  // Leaving the folder drops an unsaved new folder or rename in it. One
  // already being saved carries on; it just isn't shown here.
  useEffect(() => {
    setDraftFolder((d) => (d && d.parent !== openFolderId && !d.sending ? null : d));
    setRenaming((r) => (r && r.where !== openFolderId && !r.sending ? null : r));
  }, [openFolderId]);
  const newFolder: NewRowDraft | null = draftFolder && draftFolder.parent === openFolderId
    ? {
        key: draftFolder.key,
        initialName: draftFolder.name,
        busy: draftFolder.sending,
        error: draftFolder.error,
        onCancel: () => setDraftFolder(null),
        onCommit: (name) => {
          const { key, parent } = draftFolder;
          sameDraft(key, (d) => ({ ...d, sending: true, error: null }));
          createFolder.mutate(
            { name, parent },
            {
              onSuccess: () => sameDraft(key, () => null),
              onError: (e) => {
                // Refused after the person moved on: say so, since the row is gone.
                if (openFolderIdRef.current !== parent) {
                  sameDraft(key, () => null);
                  setToast(errorMessage(e));
                } else {
                  sameDraft(key, (d) => ({ ...d, sending: false, error: errorMessage(e) }));
                }
              },
            }
          );
        },
      }
    : null;

  // -- rename: in place, on the folder's row or the open folder's crumb -------
  const startRename = (folder: ProjectFolder) =>
    setRenaming({ folderId: folder.id, where: openFolderId, sending: false, error: null });
  const renamingFolder: FolderRename | null =
    renaming && renaming.where === openFolderId
      ? {
          folderId: renaming.folderId,
          edit: {
            busy: renaming.sending,
            error: renaming.error,
            onCancel: () => setRenaming(null),
            onCommit: (name) => {
              const { folderId, where } = renaming;
              const sameRename = (update: (r: RenameState) => RenameState | null) =>
                setRenaming((r) => (r?.folderId === folderId ? update(r) : r));
              if (name === index.byId.get(folderId)?.name) {
                setRenaming(null);
                return;
              }
              sameRename((r) => ({ ...r, sending: true, error: null }));
              renameFolder.mutate(
                { folderId, name },
                {
                  onSuccess: () => sameRename(() => null),
                  onError: (e) => {
                    // Refused after the person moved on: say so, since the field is gone.
                    if (openFolderIdRef.current !== where) {
                      sameRename(() => null);
                      setToast(errorMessage(e));
                    } else {
                      sameRename((r) => ({ ...r, sending: false, error: errorMessage(e) }));
                    }
                  },
                }
              );
            },
          },
        }
      : null;

  // -- new project: named in its row, then set up beside the map -------------
  const newProjectFlow = useNewProjectFlow();
  const projectFlow = newProjectFlow.flow;
  const startNewProject = (latlng: { lat: number; lng: number } | null = null) => {
    setMapExpanded(false);
    newProjectFlow.start(openFolderId, latlng);
  };
  const newProject: NewRowDraft | null =
    projectFlow?.phase === "naming"
      ? {
          key: projectFlow.key,
          initialName: NEW_PROJECT_NAME,
          busy: false,
          error: null,
          onCommit: newProjectFlow.commitName,
          onCancel: newProjectFlow.cancel,
        }
      : null;
  const settingUpProject = projectFlow?.phase === "steps";
  const projectFolderKey = projectFlow ? normalizeFolderId(index, projectFlow.data.folderId) : null;

  const setBrowse = (next: "projects" | "stories") => {
    if (next === browse) return;
    browseFlag.set(next === "stories" ? "stories" : null);
    if (next === "stories") {
      // A half-made project would be orphaned beside a table that no longer
      // shows it.
      if (projectFlow) newProjectFlow.cancel();
      setDraftFolder((d) => (d && !d.sending ? null : d));
      setRenaming((r) => (r && !r.sending ? null : r));
    }
  };

  // -- story actions (browse mode) ----------------------------------------------
  const deleteStory = useDeleteStoryMap();
  const openStory = useCallback((story: StoryMapSummary) => navigate(storyMapPaths.builder(story)), [navigate]);
  const previewStory = useCallback((story: StoryMapSummary) => navigate(storyMapPaths.preview(story)), [navigate]);
  const copyShareLink = useCallback(async (story: StoryMapSummary) => {
    if (!story.slug) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/s/${story.slug}`);
      setNotice("Share link copied");
    } catch {
      setToast("Couldn't copy the link. Open the story and copy it from Share.");
    }
  }, []);
  const locateStory = useCallback((story: StoryMapSummary) => flyToBounds(storyExtentFor(story)), [flyToBounds, storyExtentFor]);
  const openStoryStack = (title: string, stories: StoryMapSummary[], anchorEl: HTMLElement) =>
    openDialog({ kind: "storyStack", title, stories, anchorEl });

  // -- the Archive: archive by drag or menu, restore, delete for good -----------
  const archiveProject = useArchiveProject();
  const restoreProject = useRestoreProject();
  const deleteProjectForever = usePermanentlyDeleteProject();
  const canDeletePermanently = !viewOnly && (!!user?.is_staff || hasCapability(user, "delete_project"));
  const archivedExtent = useMemo(() => bboxOfProjects(archivedAll), [archivedAll]);
  const archivePlace = {
    open: archiveOpen,
    count: archivedAll.length,
    canLocate: archivedExtent !== null,
    onOpen: openArchive,
    onLocate: () => flyToBounds(archivedExtent),
  };

  // -- new story map in the open folder (or across every project) -----------------
  const createStory = useCreateStoryMapScoped();
  const storyTemplateContext = useMemo<StoryTemplateContext>(() => {
    let members = everyProject;
    if (openFolderId !== null) {
      const ids = descendantIds(index, openFolderId);
      ids.add(openFolderId);
      members = everyProject.filter((p) => {
        const f = normalizeFolderId(index, p.folder);
        return f !== null && ids.has(f);
      });
    }
    const folderName = openFolderId === null ? undefined : index.byId.get(openFolderId)?.name;
    return {
      authorName: user?.username,
      scope: openFolderId === null ? "root" : "folder",
      projectName: folderName,
      folderName,
      projects: members
        .filter((p) => !p.is_archived)
        .map((p) => ({
          id: Number(p.id),
          name: p.name,
          bbox: projectBbox(p),
          operationalStrategyId: p.operational_strategy ?? null,
          updatedAt: p.updated ?? null,
        })),
      boundary: null,
      strategies: [],
      simulations: [],
      today: new Date().toISOString().slice(0, 10),
      unitSystem: user?.unit_system ?? "metric",
    };
  }, [everyProject, openFolderId, index, user?.username, user?.unit_system]);
  const createStoryFrom = (title: string, content: StoryContent) =>
    createStory.mutate(
      { title, content, folder_id: openFolderId },
      {
        onSuccess: (res) => {
          closeDialog();
          navigate(storyMapPaths.builder(res.data));
        },
        onError: (e) => {
          closeDialog();
          setToast(errorMessage(e));
        },
      }
    );

  const moveProjectTo = useCallback(
    (projectId: string, target: FolderKey, onDone?: () => void) =>
      moveProject.mutate(
        { projectId, folderId: target },
        { onSuccess: onDone, onError: (e) => setToast(errorMessage(e)) }
      ),
    [moveProject]
  );
  const moveFolderTo = useCallback(
    (folderId: number, target: FolderKey, onDone?: () => void, onFail?: (msg: string) => void) =>
      updateFolder.mutate(
        { folderId, parent: target },
        {
          onSuccess: onDone,
          onError: (e) => (onFail ? onFail(errorMessage(e)) : setToast(errorMessage(e))),
        }
      ),
    [updateFolder]
  );

  // What the right-hand pane browses, switched from the breadcrumbs strip
  // above the table (owner's placement 2026-10-09).
  const browseToggle = (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={browse}
      onChange={(_e, v: "projects" | "stories" | null) => {
        if (v) setBrowse(v);
      }}
      data-tour={LISTING_TOUR_ANCHOR.browseToggle}
      aria-label="What to browse"
      sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.5, py: 0.25 } }}
    >
      <ToggleButton value="projects">Projects</ToggleButton>
      <ToggleButton value="stories">Story maps</ToggleButton>
    </ToggleButtonGroup>
  );

  const currentFolder = openFolderId === null ? null : index.byId.get(openFolderId) ?? null;
  const folderActions =
    currentFolder && !viewOnly
      ? {
          onRename: () => startRename(currentFolder),
          onMove: () => openDialog({ kind: "moveFolder", folder: currentFolder }),
          onDelete: () => openDialog({ kind: "deleteFolder", folder: currentFolder }),
          onBoundary: () => openDialog({ kind: "folderBoundary", folder: currentFolder }),
          hasBoundary: !!currentFolder.boundary_bbox,
        }
      : null;

  const deleteTarget = dialog?.kind === "deleteFolder" ? dialog.folder : null;
  // Story counts are only known once the story list has loaded (browse mode).
  const deleteCounts = deleteTarget
    ? subtreeCounts(index, all, deleteTarget.id, storiesQuery.data ? storyCountFor : undefined)
    : null;
  const deleteParentName =
    deleteTarget?.parent != null ? index.byId.get(deleteTarget.parent)?.name ?? "the folder above" : "All projects";

  const pickerDisabledIds: ReadonlySet<number> =
    dialog?.kind === "moveFolder" ? new Set([dialog.folder.id, ...descendantIds(index, dialog.folder.id)]) : EMPTY_IDS;
  const pickerCurrent: FolderKey =
    dialog?.kind === "moveFolder"
      ? normalizeFolderId(index, dialog.folder.parent)
      : dialog?.kind === "moveProject"
        ? normalizeFolderId(index, dialog.project.folder)
        : null;

  return (
    <Box sx={{ height: "calc(var(--vh100) - var(--topbar-h))", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          borderBottom: 1,
          borderColor: "divider",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder={browse === "stories" ? "Search story maps..." : "Search projects..."}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 220 }}
        />
        <Autocomplete
          multiple
          size="small"
          options={currentTags}
          value={selectedTags}
          onChange={(_e, v: string[]) => setSelectedTags(v)}
          renderInput={(p) => <TextField {...p} placeholder="Filter by tags..." sx={{ width: 240 }} />}
          renderTags={(value, getTagProps) =>
            value.map((option, i) => (
              <Chip
                {...getTagProps({ index: i })}
                key={option}
                label={option}
                size="small"
                sx={{ backgroundColor: "#e0f2fe", color: "#0369a1" }}
              />
            ))
          }
        />
        <NewProjectMenu
          folderId={openFolderId}
          canCreateProject={canCreateProject && browse === "projects"}
          canCreateFolder={!viewOnly}
          onNewFolder={startNewFolder}
          onNewProject={() => startNewProject()}
          canCreateStoryMap={browse === "stories" && !viewOnly}
          onNewStoryMap={() => openDialog({ kind: "newStory" })}
          disabled={settingUpProject || archiveOpen}
        />
        {layoutToggle}
        <Tooltip title="Show me around">
          <IconButton data-tour={LISTING_TOUR_ANCHOR.help} onClick={tour.start} aria-label="Show me around">
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* One map context for both panes: the new-project panel's place search
          flies the map on the left. */}
      <MapProvider>
          <Box
            ref={gridRef}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "grid",
              gridTemplateColumns,
              gridTemplateRows: "minmax(0, 1fr)",
            }}
          >
            <Box
              data-tour={LISTING_TOUR_ANCHOR.map}
              sx={{ position: "relative", minWidth: 0, minHeight: 0, overflow: "hidden" }}
            >
              <DrawingProvider>
                <ProjectListingMapContent
                  projects={drawnProjects}
                  lookupProjects={visibleProjects}
                  onViewportChange={setViewport}
                  isLoading={archiveOpen ? archivedLoading : isLoading}
                  error={error ?? null}
                  fillParent
                  layerPanelPlacement="absolute"
                  layerPanelSide="left"
                  hoveredProjectId={hoveredProjectId}
                  onHoverProject={onMapHoverProject}
                  flyTo={flyTo}
                  folders={folderOverlays}
                  openFolderBoundary={openFolderBoundary}
                  hoveredFolderId={hoveredFolderId}
                  onHoverFolder={onMapHoverFolder}
                  onOpenFolder={openFolder}
                  fitKey={fitKey}
                  fitBounds={fitTarget}
                  homeBounds={homeBounds}
                  onMapReady={() => setMapReady(true)}
                  onPlacementModeChange={(active) => {
                    placementActiveRef.current = active;
                  }}
                  createInFolderId={openFolderId}
                  onPickNewProjectLocation={(latlng) =>
                    projectFlow ? newProjectFlow.placeAt(latlng) : startNewProject(latlng)
                  }
                  newProject={newProjectFlow.onMap}
                  emptyMessage={
                    archiveOpen
                      ? "Nothing archived"
                      : mode === "search"
                        ? "No projects match your search"
                        : "No projects in this folder"
                  }
                  projectClicksEnabled={browse === "projects" && !archiveOpen}
                  createFromMapEnabled={browse === "projects" && !archiveOpen}
                  storyGroups={storyGroups}
                  hoveredGroupKeys={hoveredGroupKeys}
                  onHoverStoryGroup={onMapHoverStoryGroup}
                  onOpenStory={(id) => {
                    const story = storyIndex.byId.get(id);
                    if (story) openStory(story);
                  }}
                  onOpenStoryStack={(group, el) => openStoryStack(group.label, group.stories, el)}
                  topRightSlot={
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip
                        title={
                          onlyInView
                            ? "Listing only what's on the map. Click to list everything"
                            : "List only the projects and folders on the map"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => setOnlyInView((v) => !v)}
                          aria-label={onlyInView ? "List everything" : "List only what's on the map"}
                          sx={{
                            backgroundColor: onlyInView ? "#00d4ad" : "rgba(30, 41, 59, 0.9)",
                            color: onlyInView ? "#0b1119" : "#f8fafc",
                            "&:hover": { backgroundColor: onlyInView ? "#00bd9a" : "rgba(30, 41, 59, 1)" },
                          }}
                        >
                          {onlyInView ? <FilterAltIcon /> : <FilterAltOffIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={mapExpanded ? "Show the list (Esc)" : "Make the map fill the screen"}>
                        <IconButton
                          data-tour={LISTING_TOUR_ANCHOR.expand}
                          size="small"
                          onClick={() => setMapExpanded((v) => !v)}
                          aria-label={mapExpanded ? "Show the list" : "Expand the map"}
                          sx={{
                            backgroundColor: "rgba(30, 41, 59, 0.9)",
                            color: "#f8fafc",
                            "&:hover": { backgroundColor: "rgba(30, 41, 59, 1)" },
                          }}
                        >
                          {mapExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
              </DrawingProvider>
              {browse === "stories" && (
                <RootStoriesCard
                  stories={rootStories}
                  highlighted={hoveredGroupKeys.has(ROOT_GROUP_KEY)}
                  onHover={(h) => onMapHoverStoryGroup(h ? ROOT_GROUP_KEY : null)}
                  onOpenStory={openStory}
                  onOpenStack={(el) => openStoryStack("All projects", rootStories, el)}
                />
              )}
            </Box>
            <SplitHandle
              containerRef={gridRef}
              onChange={setRatio}
              onCommit={(r) => splitFlag.set(Math.round(r * 100) / 100)}
              onReset={() => {
                setRatio(SPLIT_DEFAULT);
                splitFlag.set(SPLIT_DEFAULT);
              }}
              hidden={mapExpanded}
            />
            <Box
              data-tour={LISTING_TOUR_ANCHOR.table}
              sx={{
                minWidth: 0,
                minHeight: 0,
                display: mapExpanded ? "none" : "flex",
                flexDirection: "column",
                overflow: "hidden",
                ...(dragging && { "& *": { cursor: "grabbing" } }),
              }}
            >
              {browse === "stories" ? (
                <StoryTablePane
                  mode={mode}
                  index={index}
                  openFolderId={openFolderId}
                  folders={listedFolders}
                  stories={listedStoryRows}
                  storyIndex={storyIndex}
                  allProjects={all}
                  isLoading={isLoading || storiesQuery.isLoading}
                  error={error ?? storiesQuery.error ?? null}
                  highlightedStoryIds={highlightedStoryIds}
                  highlightedFolderId={hoveredFolderId}
                  scrollToStoryId={scrollToStoryId}
                  sort={storySort}
                  onSortChange={(s: StorySortSpec | null) => storySortFlag.set(serializeStorySort(s))}
                  viewOnly={viewOnly}
                  searchSummary={{
                    count: visibleStories.length,
                    onClear: () => {
                      setSearchQuery("");
                      setSelectedTags([]);
                    },
                  }}
                  folderActions={folderActions}
                  onOpenFolder={openFolder}
                  onHoverStory={onTableHoverStory}
                  onHoverFolder={onTableHoverFolder}
                  onOpenStory={openStory}
                  onPreviewStory={previewStory}
                  onCopyShareLink={copyShareLink}
                  onDeleteStory={(story) => openDialog({ kind: "deleteStory", story })}
                  storyHasLocation={(story) => storyExtentFor(story) !== null}
                  onLocateStory={locateStory}
                  onRequestRenameFolder={startRename}
                  onRequestMoveFolder={(folder) => openDialog({ kind: "moveFolder", folder })}
                  onRequestDeleteFolder={(folder) => openDialog({ kind: "deleteFolder", folder })}
                  onRequestFolderBoundary={(folder) => openDialog({ kind: "folderBoundary", folder })}
                  onToggleFolderMapVisible={(folder) =>
                    updateFolder.mutate(
                      { folderId: folder.id, map_visible: folder.map_visible === false },
                      { onError: (e) => setToast(errorMessage(e)) }
                    )
                  }
                  onToggleFolderPin={(folder) =>
                    updateFolder.mutate(
                      { folderId: folder.id, is_pinned: !folder.is_pinned },
                      { onError: (e) => setToast(errorMessage(e)) }
                    )
                  }
                  onLocateFolder={(folder) => flyToBounds(folderFlyExtentFor(folder.id))}
                  folderHasLocation={(folder) => folderExtentFor(folder.id, true) !== null}
                  renamingFolder={renamingFolder}
                  viewFilter={storyViewFilter}
                  headerEnd={browseToggle}
                  archive={archiveOpen ? archivePlace : undefined}
                />
              ) : settingUpProject && projectFlow ? (
                <CreateProjectPane
                  key={projectFlow.key}
                  data={projectFlow.data}
                  step={projectFlow.step}
                  onStepChange={newProjectFlow.setStep}
                  onUpdateData={newProjectFlow.updateData}
                  area={newProjectFlow.area}
                  folderLabel={pathLabel(index, projectFolderKey) || "All projects"}
                  onCancel={newProjectFlow.cancel}
                  onCreated={() => {
                    newProjectFlow.cancel();
                    // Back to the list where the new project was filed.
                    if (projectFolderKey !== openFolderId) openFolder(projectFolderKey);
                  }}
                />
              ) : (
                <ProjectTablePane
                  mode={mode}
                  index={index}
                  openFolderId={openFolderId}
                  folders={listedFolders}
                  projects={listedRows}
                  allProjects={all}
                  isLoading={archiveOpen ? archivedLoading : isLoading}
                  error={error ?? null}
                  highlightedProjectId={hoveredProjectId}
                  highlightedFolderId={hoveredFolderId}
                  scrollToProjectId={scrollToProjectId}
                  sort={sort}
                  onSortChange={(s: SortSpec | null) => sortFlag.set(serializeSort(s))}
                  viewOnly={viewOnly}
                  searchSummary={{
                    count: visibleProjects.length,
                    onClear: () => {
                      setSearchQuery("");
                      setSelectedTags([]);
                    },
                  }}
                  folderActions={folderActions}
                  onOpenFolder={openFolder}
                  onHoverProject={onTableHoverProject}
                  onHoverFolder={onTableHoverFolder}
                  onDraggingChange={onDraggingChange}
                  onMoveProject={(projectId, target) => moveProjectTo(projectId, target)}
                  onMoveFolder={(folderId, target) => moveFolderTo(folderId, target)}
                  onRequestRenameFolder={startRename}
                  onRequestMoveFolder={(folder) => openDialog({ kind: "moveFolder", folder })}
                  onRequestDeleteFolder={(folder) => openDialog({ kind: "deleteFolder", folder })}
                  onRequestFolderBoundary={(folder) => openDialog({ kind: "folderBoundary", folder })}
                  onRequestMoveProject={(project) => openDialog({ kind: "moveProject", project })}
                  onLocateProject={locateProject}
                  onRequestPropertyStyle={(project) => openDialog({ kind: "propertyStyle", project })}
                  onToggleProjectMapVisible={(project) =>
                    setProjectMapVisible.mutate(
                      { projectId: String(project.id), mapVisible: project.map_visible === false },
                      { onError: (e) => setToast(errorMessage(e)) }
                    )
                  }
                  onToggleFolderMapVisible={(folder) =>
                    updateFolder.mutate(
                      { folderId: folder.id, map_visible: folder.map_visible === false },
                      { onError: (e) => setToast(errorMessage(e)) }
                    )
                  }
                  onToggleFolderPin={(folder) =>
                    updateFolder.mutate(
                      { folderId: folder.id, is_pinned: !folder.is_pinned },
                      { onError: (e) => setToast(errorMessage(e)) }
                    )
                  }
                  onLocateFolder={(folder) => flyToBounds(folderFlyExtentFor(folder.id))}
                  folderHasLocation={(folder) => folderExtentFor(folder.id, true) !== null}
                  newFolder={newFolder}
                  newProject={newProject}
                  renamingFolder={renamingFolder}
                  viewFilter={viewFilter}
                  headerEnd={browseToggle}
                  archive={{
                    ...archivePlace,
                    onArchiveProject: (projectId) =>
                      archiveProject.mutate(projectId, { onError: (e) => setToast(errorMessage(e)) }),
                    onRestoreProject: (project) =>
                      restoreProject.mutate(String(project.id), {
                        onSuccess: () => setNotice(`“${project.name}” restored`),
                        onError: (e) => setToast(errorMessage(e)),
                      }),
                    onDeleteProjectPermanently: (project) => openDialog({ kind: "deleteProjectForever", project }),
                    canDeletePermanently,
                  }}
                />
              )}
            </Box>
          </Box>
        </MapProvider>

      <FolderBoundaryDialog
        open={dialog?.kind === "folderBoundary"}
        // The live row, so the dialog shows the new boundary as soon as it is set.
        folder={dialog?.kind === "folderBoundary" ? index.byId.get(dialog.folder.id) ?? dialog.folder : null}
        onClose={closeDialog}
      />
      <PropertyStyleDialog
        open={dialog?.kind === "propertyStyle"}
        // The live row, so the dialog's sliders start from what is saved.
        project={
          dialog?.kind === "propertyStyle"
            ? all.find((p) => String(p.id) === String(dialog.project.id)) ?? dialog.project
            : null
        }
        onClose={closeDialog}
      />
      <FolderPickerDialog
        open={dialog?.kind === "moveFolder" || dialog?.kind === "moveProject"}
        title={
          dialog?.kind === "moveFolder"
            ? `Move “${dialog.folder.name}” to…`
            : dialog?.kind === "moveProject"
              ? `Move “${dialog.project.name}” to…`
              : "Move to…"
        }
        index={index}
        disabledIds={pickerDisabledIds}
        currentId={pickerCurrent}
        busy={updateFolder.isPending || moveProject.isPending}
        error={dialogError}
        onClose={closeDialog}
        onSelect={(target) => {
          if (dialog?.kind === "moveFolder") {
            moveFolderTo(dialog.folder.id, target, closeDialog, setDialogError);
          } else if (dialog?.kind === "moveProject") {
            moveProject.mutate(
              { projectId: String(dialog.project.id), folderId: target },
              { onSuccess: closeDialog, onError: (e) => setDialogError(errorMessage(e)) }
            );
          }
        }}
      />
      <ConfirmDeleteModal
        open={dialog?.kind === "deleteFolder"}
        onClose={closeDialog}
        title="Delete folder"
        confirmLabel="Delete folder"
        warningText=""
        isLoading={deleteFolder.isPending}
        message={
          deleteTarget && deleteCounts ? (
            <Typography variant="body1">
              “{deleteTarget.name}” will be deleted.{" "}
              {deleteCounts.projects + deleteCounts.folders + (deleteCounts.stories ?? 0) > 0
                ? `Its ${[
                    deleteCounts.projects ? `${deleteCounts.projects} project${deleteCounts.projects === 1 ? "" : "s"}` : null,
                    deleteCounts.folders ? `${deleteCounts.folders} folder${deleteCounts.folders === 1 ? "" : "s"}` : null,
                    deleteCounts.stories ? `${deleteCounts.stories} story map${deleteCounts.stories === 1 ? "" : "s"}` : null,
                  ]
                    .filter(Boolean)
                    .join(" and ")} will move to “${deleteParentName}”.`
                : "It is empty."}
            </Typography>
          ) : null
        }
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteFolder.mutate(deleteTarget.id, {
            onSuccess: (res) => {
              closeDialog();
              // Standing inside the deleted folder (or beneath it): step up
              // to where its contents went.
              if (openFolderId !== null && (openFolderId === deleteTarget.id || isDescendant(index, openFolderId, deleteTarget.id))) {
                openFolder(res?.data?.parent ?? null);
              }
            },
            onError: (e) => {
              closeDialog();
              setToast(errorMessage(e));
            },
          });
        }}
      />
      <ConfirmDeleteModal
        open={dialog?.kind === "deleteStory"}
        onClose={closeDialog}
        title="Delete story map"
        confirmLabel="Delete story map"
        warningText=""
        isLoading={deleteStory.isPending}
        message={
          dialog?.kind === "deleteStory" ? (
            <Typography variant="body1">
              “{dialog.story.title}” will be deleted
              {dialog.story.status === "PUBLISHED" ? ", and its share link will stop working" : ""}. This cannot be
              undone.
            </Typography>
          ) : null
        }
        onConfirm={() => {
          if (dialog?.kind !== "deleteStory") return;
          deleteStory.mutate(dialog.story.id, {
            onSuccess: closeDialog,
            onError: (e) => {
              closeDialog();
              setToast(errorMessage(e));
            },
          });
        }}
      />
      <ConfirmDeleteModal
        open={dialog?.kind === "deleteProjectForever"}
        onClose={closeDialog}
        title="Delete project permanently"
        confirmLabel="Delete permanently"
        loadingLabel="Deleting…"
        warningText="This cannot be undone."
        isLoading={deleteProjectForever.isPending}
        message={
          dialog?.kind === "deleteProjectForever" ? (
            <Typography variant="body1">
              “{dialog.project.name}” and everything in it — strategies, simulations, story maps and data —
              will be deleted for good.
            </Typography>
          ) : null
        }
        onConfirm={() => {
          if (dialog?.kind !== "deleteProjectForever") return;
          deleteProjectForever.mutate(String(dialog.project.id), {
            onSuccess: closeDialog,
            onError: (e) => {
              closeDialog();
              setToast(errorMessage(e));
            },
          });
        }}
      />
      <NewStoryDialog
        open={dialog?.kind === "newStory"}
        onClose={closeDialog}
        context={storyTemplateContext}
        onCreate={createStoryFrom}
        creating={createStory.isPending}
      />
      <StoryStackPopover
        anchorEl={dialog?.kind === "storyStack" ? dialog.anchorEl : null}
        title={dialog?.kind === "storyStack" ? dialog.title : ""}
        stories={dialog?.kind === "storyStack" ? dialog.stories : EMPTY_STORIES}
        onClose={closeDialog}
        onOpenStory={openStory}
      />
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setToast(null)} variant="filled">
          {toast}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!notice}
        autoHideDuration={3000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setNotice(null)} variant="filled">
          {notice}
        </Alert>
      </Snackbar>
    </Box>
  );
}
